/**
 * S-Curves & Discipline Cost Trends
 *
 * Three views:
 *   1. Cumulative S-curve by Discipline (stacked area, 7 disciplines)
 *   2. Month-on-month cost trend by Phase (8 phases) with sub-phase drill-down
 *   3. Detailed sub-discipline view (drill-down into any selected discipline)
 *
 * Optional `actuals` prop overlays a planned-vs-actual line on view 1.
 */
import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ComposedChart,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { TrendingUp, Layers, Activity, ChevronLeft } from 'lucide-react';
import { makeFmt } from './currency';

const fmt$Default = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtK$Default = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
};

const DISCIPLINE_META = {
  project_mgmt:          { label: 'Project Management',      color: '#0ea5e9' },
  civil_structural:      { label: 'Civil & Structural',      color: '#10b981' },
  mep:                   { label: 'MEP',                     color: '#f59e0b' },
  architecture_envelope: { label: 'Architecture & Envelope', color: '#a855f7' },
  finishes_interiors:    { label: 'Finishes & Interiors',    color: '#ec4899' },
  logistics_equipment:   { label: 'Logistics & Equipment',   color: '#64748b' },
  general_works:         { label: 'General Works',           color: '#475569' },
};

const PHASE_PALETTE = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#84cc16'];

const VIEWS = [
  { key: 'discipline', label: 'By discipline (S-curve)',  icon: Layers     },
  { key: 'phase',      label: 'By phase (monthly trend)', icon: TrendingUp },
];

export default function SCurves({ scurve, phases, subDisciplineSummary, actuals = null, calculation = null }) {
  const fmt$ = useMemo(() => calculation ? makeFmt(calculation) : fmt$Default, [calculation]);
  const fmtK$ = useMemo(() => {
    if (!calculation) return fmtK$Default;
    return (n) => makeFmt(calculation)(n, { compact: true });
  }, [calculation]);
  const [view, setView]               = useState('discipline');
  const [selectedDisc, setSelectedDisc] = useState(null);   // for sub-discipline drill-down
  const [selectedPhase, setSelectedPhase] = useState(null); // for sub-phase drill-down

  if (!scurve || !scurve.months) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500 text-center" data-testid="scurve-empty">
          Run a calculation first to see discipline-wise costs and S-curves.
        </CardContent>
      </Card>
    );
  }

  const months = scurve.months;
  const labels = scurve.month_labels || Array.from({ length: months }, (_, i) => `M${i + 1}`);

  // ---------------------------------------------------------------- *
  //  View 1 — Cumulative S-curve by discipline (stacked area)
  // ---------------------------------------------------------------- */
  const cumulativeData = useMemo(() => {
    const cby = scurve.cumulative_by_discipline || {};
    return labels.map((m, i) => {
      const row = { month: m };
      Object.keys(DISCIPLINE_META).forEach((dk) => {
        row[dk] = (cby[dk] || [])[i] || 0;
      });
      row.total = (scurve.cumulative_total || [])[i] || 0;
      return row;
    });
  }, [scurve, labels]);

  // Optional actuals overlay (planned vs actual cumulative)
  const actualOverlay = useMemo(() => {
    if (!actuals || !Array.isArray(actuals.cumulative_actual)) return null;
    return labels.map((m, i) => ({
      month: m,
      plan:    (scurve.cumulative_total || [])[i] || 0,
      actual:  actuals.cumulative_actual[i] ?? null,
    }));
  }, [actuals, scurve, labels]);

  // ---------------------------------------------------------------- *
  //  View 2 — Monthly trend by phase
  // ---------------------------------------------------------------- */
  const phaseList = useMemo(() => (phases || []).map((p, i) => ({
    key: p.phase, label: p.phase_label, color: PHASE_PALETTE[i % PHASE_PALETTE.length],
  })), [phases]);

  const phaseMonthlyData = useMemo(() => {
    const bp = scurve.by_phase || {};
    return labels.map((m, i) => {
      const row = { month: m };
      phaseList.forEach((p) => { row[p.key] = (bp[p.key] || [])[i] || 0; });
      return row;
    });
  }, [scurve, labels, phaseList]);

  // ---------------------------------------------------------------- *
  //  Drill-down: sub-discipline of selected discipline
  // ---------------------------------------------------------------- */
  const subDiscData = useMemo(() => {
    if (!selectedDisc) return null;
    const bsd = scurve.by_sub_discipline || {};
    // Find sub-disciplines belonging to selectedDisc by checking which keys
    // have non-zero values AND exist on the per-phase costs.
    const phaseMap = (phases || [])
      .flatMap((p) => Object.keys(p.sub_discipline_costs || {}));
    const allSubs = Array.from(new Set([
      ...Object.keys(bsd),
      ...phaseMap,
    ]));
    // Use sub→discipline mapping by checking which subs have data for this discipline.
    // Since the backend rolls up sub→discipline server-side, we use per-phase sub costs
    // to determine membership: a sub belongs to discipline X iff the calculator's
    // discipline_costs for that phase includes X and the same sub appears in that phase.
    // Simpler: just include all subs whose monthly array sums to >0 AND the sub key
    // prefix maps to the discipline (we know prefixes: pm_, cs_, mep_, ae_, fi_, le_, gw_).
    const PREFIX_TO_DISC = {
      pm: 'project_mgmt',
      cs: 'civil_structural',
      mep: 'mep',
      ae: 'architecture_envelope',
      fi: 'finishes_interiors',
      le: 'logistics_equipment',
      gw: 'general_works',
    };
    const subsForDisc = allSubs.filter((sk) => {
      const prefix = sk.startsWith('mep_') ? 'mep' : sk.split('_')[0];
      return PREFIX_TO_DISC[prefix] === selectedDisc;
    }).filter((sk) => {
      const arr = bsd[sk] || [];
      return arr.some((v) => v > 0.01);
    });
    if (!subsForDisc.length) return { subs: [], rows: [] };
    const palette = ['#10b981', '#0ea5e9', '#f59e0b', '#ef4444', '#a855f7', '#ec4899', '#64748b', '#84cc16'];
    const subs = subsForDisc.map((sk, i) => ({
      key: sk, label: prettify(sk), color: palette[i % palette.length],
    }));
    const rows = labels.map((m, i) => {
      const row = { month: m };
      subs.forEach((s) => { row[s.key] = (bsd[s.key] || [])[i] || 0; });
      return row;
    });
    return { subs, rows };
  }, [selectedDisc, scurve, phases, labels]);

  // ---------------------------------------------------------------- *
  //  Drill-down: sub-phase of selected phase
  // ---------------------------------------------------------------- */
  const subPhaseData = useMemo(() => {
    if (!selectedPhase) return null;
    const bsp = scurve.by_sub_phase || {};
    const keys = Object.keys(bsp).filter((k) => k.startsWith(`${selectedPhase}:`));
    if (!keys.length) return { subs: [], rows: [] };
    const phase = (phases || []).find((p) => p.phase === selectedPhase);
    const wbsLabels = Object.fromEntries((phase?.wbs || []).map((w) => [w.key, w.label]));
    const palette = ['#10b981', '#0ea5e9', '#f59e0b', '#ef4444', '#a855f7', '#ec4899', '#64748b', '#84cc16', '#14b8a6'];
    const subs = keys.map((k, i) => ({
      key: k,
      label: wbsLabels[k.split(':')[1]] || prettify(k.split(':')[1] || ''),
      color: palette[i % palette.length],
    }));
    const rows = labels.map((m, i) => {
      const row = { month: m };
      subs.forEach((s) => { row[s.key] = (bsp[s.key] || [])[i] || 0; });
      return row;
    });
    return { subs, rows };
  }, [selectedPhase, scurve, phases, labels]);

  // ---------------------------------------------------------------- *
  //  Discipline summary chips (totals)
  // ---------------------------------------------------------------- */
  const discChips = useMemo(() => {
    const ds = scurve.by_discipline || {};
    return Object.keys(DISCIPLINE_META).map((k) => ({
      key: k,
      label: DISCIPLINE_META[k].label,
      color: DISCIPLINE_META[k].color,
      total: (ds[k] || []).reduce((a, b) => a + b, 0),
    })).filter((d) => d.total > 0);
  }, [scurve]);
  const grandTotal = discChips.reduce((s, d) => s + d.total, 0);

  return (
    <div className="space-y-4" data-testid="scurves-tab">
      {/* Header + view toggle */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-display font-semibold inline-flex items-center gap-1.5">
          <Activity className="h-4 w-4" /> Discipline Costs &amp; S-Curves
        </h3>
        <div className="flex gap-1 border border-border" data-testid="scurves-view-toggle">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            return (
              <Button
                key={v.key}
                size="sm"
                variant={view === v.key ? 'default' : 'ghost'}
                className={`h-8 px-3 font-mono text-[10px] uppercase tracking-[0.15em] ${
                  view === v.key ? 'bg-foreground text-background' : ''
                }`}
                onClick={() => { setView(v.key); setSelectedDisc(null); setSelectedPhase(null); }}
                data-testid={`scurves-view-${v.key}`}
              >
                <Icon className="h-3.5 w-3.5 mr-1.5" /> {v.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Discipline chips (always visible) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-mono uppercase tracking-[0.2em] text-muted-foreground">
            Discipline split — total {fmt$(grandTotal)}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {discChips.map((d) => {
            const pct = grandTotal ? (d.total / grandTotal) * 100 : 0;
            return (
              <button
                key={d.key}
                className={`text-left border p-3 hover:border-foreground transition-colors ${
                  selectedDisc === d.key ? 'border-foreground bg-muted' : 'border-border'
                }`}
                onClick={() => { setView('discipline'); setSelectedDisc(d.key); }}
                data-testid={`scurves-discipline-chip-${d.key}`}
              >
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 inline-block" style={{ backgroundColor: d.color }} />
                  <span className="text-[11px] font-semibold leading-tight">{d.label}</span>
                </div>
                <div className="mt-2 font-display text-lg font-bold tabular-nums" style={{ color: d.color }}>
                  {fmtK$(d.total)}
                </div>
                <div className="font-mono text-[10px] text-muted-foreground">{pct.toFixed(1)}%</div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* ============ VIEW 1 — Discipline S-curve ============ */}
      {view === 'discipline' && !selectedDisc && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Cumulative S-curve by discipline</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 380 }} data-testid="scurves-discipline-chart">
              <ResponsiveContainer>
                <AreaChart data={cumulativeData} margin={{ top: 10, right: 18, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK$} />
                  <Tooltip formatter={(v) => fmt$(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {Object.keys(DISCIPLINE_META).map((dk) => (
                    <Area
                      key={dk} type="monotone" dataKey={dk}
                      stackId="1"
                      stroke={DISCIPLINE_META[dk].color}
                      fill={DISCIPLINE_META[dk].color}
                      fillOpacity={0.65}
                      name={DISCIPLINE_META[dk].label}
                      data-testid={`scurves-area-${dk}`}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-[11px] font-mono text-muted-foreground">
              Click any discipline chip above to drill into its sub-disciplines.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Sub-discipline drill-down */}
      {view === 'discipline' && selectedDisc && subDiscData && (
        <Card data-testid="scurves-subdiscipline-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm inline-flex items-center gap-2">
              <span className="h-2 w-2 inline-block" style={{ backgroundColor: DISCIPLINE_META[selectedDisc]?.color }} />
              {DISCIPLINE_META[selectedDisc]?.label} · sub-disciplines
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={() => setSelectedDisc(null)} data-testid="scurves-back-discipline">
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
          </CardHeader>
          <CardContent>
            {subDiscData.subs.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">No cost recorded for this discipline.</div>
            ) : (
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <AreaChart data={subDiscData.rows} margin={{ top: 10, right: 18, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK$} />
                    <Tooltip formatter={(v) => fmt$(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {subDiscData.subs.map((s) => (
                      <Area
                        key={s.key} type="monotone" dataKey={s.key}
                        stackId="1" stroke={s.color} fill={s.color} fillOpacity={0.6}
                        name={s.label}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Planned vs Actual overlay */}
      {actualOverlay && view === 'discipline' && !selectedDisc && (
        <Card data-testid="scurves-actuals-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Planned vs Actual cumulative</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <ComposedChart data={actualOverlay} margin={{ top: 10, right: 18, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK$} />
                  <Tooltip formatter={(v) => fmt$(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="plan"   name="Planned"  stroke="#10b981" fill="#10b981" fillOpacity={0.18} />
                  <Line type="monotone" dataKey="actual" name="Actual"   stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============ VIEW 2 — Phase monthly trend ============ */}
      {view === 'phase' && !selectedPhase && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Monthly cost trend by phase</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 380 }} data-testid="scurves-phase-chart">
              <ResponsiveContainer>
                <BarChart data={phaseMonthlyData} margin={{ top: 10, right: 18, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK$} />
                  <Tooltip formatter={(v) => fmt$(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {phaseList.map((p) => (
                    <Bar key={p.key} dataKey={p.key} stackId="phases" fill={p.color} name={p.label} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {phaseList.map((p) => (
                <Button
                  key={p.key}
                  size="sm" variant="outline"
                  className="h-7 text-[11px]"
                  onClick={() => setSelectedPhase(p.key)}
                  data-testid={`scurves-phase-drill-${p.key}`}
                >
                  <span className="h-2 w-2 inline-block mr-1.5" style={{ backgroundColor: p.color }} />
                  Drill into {p.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sub-phase drill-down */}
      {view === 'phase' && selectedPhase && subPhaseData && (
        <Card data-testid="scurves-subphase-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm inline-flex items-center gap-2">
              {(phases || []).find((p) => p.phase === selectedPhase)?.phase_label || selectedPhase} · sub-phases
              <Badge variant="outline" className="ml-1 text-[10px]">{subPhaseData.subs.length} packages</Badge>
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={() => setSelectedPhase(null)} data-testid="scurves-back-phase">
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
          </CardHeader>
          <CardContent>
            {subPhaseData.subs.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">No sub-phase data.</div>
            ) : (
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <LineChart data={subPhaseData.rows} margin={{ top: 10, right: 18, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK$} />
                    <Tooltip formatter={(v) => fmt$(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {subPhaseData.subs.map((s) => (
                      <Line
                        key={s.key} type="monotone" dataKey={s.key}
                        stroke={s.color} strokeWidth={2} dot={false}
                        name={s.label}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function prettify(key) {
  if (!key) return '';
  return key.split('_').slice(1).join(' ').replace(/\b\w/g, (c) => c.toUpperCase()) || key;
}
